import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { adminApi, AdminApiError, type UserData } from '../services/adminApi'

interface AdminAuthState {
  user: UserData | null
  token: string | null
  isLoading: boolean
  isAuthenticated: boolean
  error: string | null
}

type AdminAuthAction =
  | { type: 'LOGIN_START' }
  | { type: 'LOGIN_SUCCESS'; payload: { user: UserData; token: string } }
  | { type: 'LOGIN_FAILURE'; payload: string }
  | { type: 'LOGOUT' }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_USER'; payload: UserData }

const initialState: AdminAuthState = {
  user: null,
  token: null,
  isLoading: true,
  isAuthenticated: false,
  error: null
}

function adminAuthReducer(state: AdminAuthState, action: AdminAuthAction): AdminAuthState {
  switch (action.type) {
    case 'LOGIN_START':
      return {
        ...state,
        isLoading: true,
        error: null
      }
    case 'LOGIN_SUCCESS':
      return {
        ...state,
        user: action.payload.user,
        token: action.payload.token,
        isAuthenticated: true,
        isLoading: false,
        error: null
      }
    case 'LOGIN_FAILURE':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: action.payload
      }
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false,
        error: null
      }
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      }
    case 'CLEAR_ERROR':
      return {
        ...state,
        error: null
      }
    case 'SET_USER':
      return {
        ...state,
        user: action.payload
      }
    default:
      return state
  }
}

interface AdminAuthContextType extends AdminAuthState {
  login: (email: string, password: string) => Promise<boolean>
  logout: () => Promise<void>
  clearError: () => void
  checkAuth: () => Promise<void>
  updateUser: (userData: Partial<UserData>) => void
}

const AdminAuthContext = createContext<AdminAuthContextType | undefined>(undefined)

export function AdminAuthProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(adminAuthReducer, initialState)

  // Check authentication on mount
  useEffect(() => {
    checkAuth()
  }, [])

  const checkAuth = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true })
      
      if (!adminApi.isAuthenticated()) {
        dispatch({ type: 'LOGOUT' })
        return
      }

      const sessionData = await adminApi.getSession()
      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: sessionData.user,
          token: localStorage.getItem('admin_token') || ''
        }
      })
    } catch (error) {
      console.error('Auth check failed:', error)
      adminApi.clearAuth()
      dispatch({ type: 'LOGOUT' })
    }
  }

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      dispatch({ type: 'LOGIN_START' })
      
      const response = await adminApi.login({ email, password })
      
      // Convert login response user to UserData format
      const userData: UserData = {
        ...response.user,
        role: response.user.role as 'USER' | 'ADMIN' | 'SUPER_ADMIN',
        isActive: true, // User must be active to login
        emailVerified: true, // User must be verified to login  
        createdAt: new Date().toISOString(), // We'll get this from session call
        updatedAt: new Date().toISOString(),
        company: undefined,
        jobTitle: undefined,
        phone: undefined,
        avatar: response.user.avatar || undefined // Ensure avatar is included
      }

      dispatch({
        type: 'LOGIN_SUCCESS',
        payload: {
          user: userData,
          token: response.token
        }
      })
      
      return true
    } catch (error) {
      const message = error instanceof AdminApiError 
        ? error.message 
        : 'Login failed. Please try again.'
      
      dispatch({ type: 'LOGIN_FAILURE', payload: message })
      return false
    }
  }

  const logout = async () => {
    try {
      await adminApi.logout()
    } catch (error) {
      console.error('Logout error:', error)
    } finally {
      dispatch({ type: 'LOGOUT' })
    }
  }

  const clearError = () => {
    dispatch({ type: 'CLEAR_ERROR' })
  }

  const updateUser = (userData: Partial<UserData>) => {
    if (state.user) {
      dispatch({
        type: 'SET_USER',
        payload: { ...state.user, ...userData }
      })
    }
  }

  const value: AdminAuthContextType = {
    ...state,
    login,
    logout,
    clearError,
    checkAuth,
    updateUser
  }

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  )
}

export function useAdminAuth() {
  const context = useContext(AdminAuthContext)
  if (context === undefined) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider')
  }
  return context
}

export type { AdminAuthState, UserData }